/**
 * LA RENTABILITÉ, CÔTÉ ÉCRAN — lot 102, 18/09/2026.
 *
 * L'API rend UN MOIS déjà calculé (les clés de répartition sont dans
 * `@stafa/core/rentabilite`, partagées avec les tests). Ici : le cumul de
 * plusieurs mois, les lignes de trajet à la maille choisie, et les formats.
 */

import {
  lignesDeTrajet,
  POSTES_REELS,
  type LigneRenta,
  type LigneTrajet,
  type Maille,
  type PointMort,
  type PosteReel,
  type ReelDuMois,
  type TransportRenta,
  type VehiculeRenta,
} from '@stafa/core';

export interface SalaireLu {
  readonly chauffeur_id: string;
  readonly nom: string;
  readonly cout: number;
  readonly estime: boolean;
  readonly jours: number;
}

export interface RentabiliteLue {
  readonly aujourdhui: string;
  readonly mois: string;
  readonly du: string;
  readonly au: string;
  readonly en_cours: boolean;
  readonly sans_balance: boolean;
  readonly reel: ReelDuMois;
  readonly vehicules: readonly VehiculeRenta[];
  readonly lignes: readonly LigneRenta[];
  readonly totaux: {
    readonly ca: number;
    readonly cout_prevu: number;
    readonly cout_reel: number;
    readonly marge: number;
    readonly km: number;
    readonly km_vide: number;
  };
  readonly salaires: readonly SalaireLu[];
  readonly salaires_estimes: boolean;
  readonly attelages: readonly {
    chauffeur_id: string;
    chauffeur_nom: string;
    vehicule_id: string;
    jours: number;
  }[];
  readonly point_mort: PointMort;
  readonly trajets: readonly TransportRenta[];
  readonly objectifs: readonly { cible: string; valeur: number }[];
  readonly incalculables: readonly {
    vehicule_id: string;
    jour: string;
    pourquoi: string;
  }[];
  readonly surcharge_non_comptee: boolean;
}

/** Le coût au kilomètre COMPLET de chaque camion, pour le coût d'un trajet. */
export function coutKmParVehicule(l: RentabiliteLue): Map<string, number> {
  const m = new Map<string, number>();
  for (const x of l.lignes) {
    if (x.vehicule.km > 0) m.set(x.vehicule.id, x.cout_reel / x.vehicule.km);
  }
  return m;
}

export function trajetsDe(l: RentabiliteLue, maille: Maille): LigneTrajet[] {
  return lignesDeTrajet(l.trajets, maille, coutKmParVehicule(l));
}

export function objectifDuVehicule(
  l: RentabiliteLue,
  immatriculation: string,
): number | null {
  return l.objectifs.find((o) => o.cible === immatriculation)?.valeur ?? null;
}

/**
 * LE CUMUL DE PLUSIEURS MOIS. Les lignes s'additionnent camion par camion ;
 * un camion absent d'un mois y compte zéro. Le point mort n'est PAS cumulé :
 * c'est un seuil mensuel, et l'additionner ne voudrait rien dire.
 */
export function cumulerRentabilite(
  mois: readonly RentabiliteLue[],
): RentabiliteLue | null {
  const dernier = mois[mois.length - 1];
  if (dernier === undefined) return null;
  const parVehicule = new Map<string, LigneRenta>();
  for (const m of mois) {
    for (const l of m.lignes) {
      const deja = parVehicule.get(l.vehicule.id);
      if (deja === undefined) {
        parVehicule.set(l.vehicule.id, l);
        continue;
      }
      const v: VehiculeRenta = {
        ...deja.vehicule,
        jours: deja.vehicule.jours + l.vehicule.jours,
        km: deja.vehicule.km + l.vehicule.km,
        km_vide: deja.vehicule.km_vide + l.vehicule.km_vide,
        ca: deja.vehicule.ca + l.vehicule.ca,
        cout_prevu: deja.vehicule.cout_prevu + l.vehicule.cout_prevu,
        variable_prevu: deja.vehicule.variable_prevu + l.vehicule.variable_prevu,
        loyer_prevu: deja.vehicule.loyer_prevu + l.vehicule.loyer_prevu,
      };
      const postes = Object.fromEntries(
        POSTES_REELS.map((p) => [p.cle, deja.postes[p.cle] + l.postes[p.cle]]),
      ) as Record<PosteReel, number>;
      const coutReel = POSTES_REELS.reduce((s, p) => s + postes[p.cle], 0);
      const marge = v.ca - coutReel;
      parVehicule.set(v.id, {
        vehicule: v,
        postes,
        cout_reel: coutReel,
        ecart: coutReel - v.cout_prevu,
        marge,
        part_vide: v.km > 0 ? v.km_vide / v.km : null,
        ca_km: v.km > 0 ? Math.round(v.ca / v.km) : null,
        ca_jour: v.jours > 0 ? Math.round(v.ca / v.jours) : null,
        taux: v.ca > 0 ? marge / v.ca : null,
        marge_km: v.km > 0 ? Math.round(marge / v.km) : null,
        marge_jour: v.jours > 0 ? Math.round(marge / v.jours) : null,
      });
    }
  }
  const lignes = [...parVehicule.values()].sort((a, b) =>
    a.vehicule.immatriculation.localeCompare(b.vehicule.immatriculation),
  );
  const ajoute = (f: (m: RentabiliteLue) => number) =>
    mois.reduce((s, m) => s + f(m), 0);
  return {
    ...dernier,
    mois: `${mois[0]?.mois ?? ''} → ${dernier.mois}`,
    du: mois[0]?.du ?? dernier.du,
    reel: Object.fromEntries(
      POSTES_REELS.map((p) => [p.cle, ajoute((m) => m.reel[p.cle])]),
    ) as ReelDuMois,
    lignes,
    totaux: {
      ca: ajoute((m) => m.totaux.ca),
      cout_prevu: ajoute((m) => m.totaux.cout_prevu),
      cout_reel: ajoute((m) => m.totaux.cout_reel),
      marge: ajoute((m) => m.totaux.marge),
      km: ajoute((m) => m.totaux.km),
      km_vide: ajoute((m) => m.totaux.km_vide),
    },
    trajets: mois.flatMap((m) => m.trajets),
    incalculables: mois.flatMap((m) => m.incalculables),
    salaires: dernier.salaires,
  };
}

/** « 0,42 €/km » — un ratio au kilomètre, en centimes. */
export function euroKm(centimes: number | null): string {
  if (centimes === null) return '—';
  return `${(centimes / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €/km`;
}

export function pourcentage(v: number | null, decimales = 1): string {
  if (v === null || !Number.isFinite(v)) return '—';
  return `${(v * 100).toLocaleString('fr-FR', { maximumFractionDigits: decimales })} %`;
}

export function km(v: number): string {
  return `${v.toLocaleString('fr-FR')} km`;
}

export const NOMS_MAILLES: readonly { readonly cle: Maille; readonly nom: string }[] = [
  { cle: 'departement', nom: 'Département' },
  { cle: 'ville', nom: 'Ville' },
];
